/* Sunnykeep instant preview (front end).
 *
 * Flow: pick photo -> upload + pets-only check -> pick style -> create (20-60 s) -> reveal ->
 * order. Talks to preview-api (settings.sk_preview_api_url). With no API URL set it runs in
 * placeholder mode: same screens, fake images drawn in the browser, nothing leaves the device.
 *
 * Pixel events go through Shopify.analytics.publish (custom pixel forwards them to Meta, see
 * docs/meta-custom-pixel.js) with an event id the API reuses for the Conversions API copy.
 */
(() => {
  'use strict';

  const CONFIG = JSON.parse(document.getElementById('sk-preview-config')?.textContent || '{}');
  const API = (CONFIG.apiUrl || '').replace(/\/+$/, '');
  // Without an API URL, the theme editor shows made-up previews so the flow can be designed.
  // Shoppers never get those: on the storefront the upload says previews are unavailable.
  const PLACEHOLDER = !API && CONFIG.designMode === true;
  const MAX_SIDE = 3072;
  const STORE_KEY = 'sk:previews';
  const EMAIL_KEY = 'sk:email';
  const EXPECTED_SECONDS = 35;

  const STEPS = {
    royal: ['Studying their face and markings', 'Choosing the light', 'Painting the velvet cloak', 'Framing it on a canvas'],
    christmas: ['Studying their face and markings', 'Choosing the light', 'Adding the Santa hat', 'Framing it on a canvas'],
    'pop-art': ['Studying their face and markings', 'Picking bold colours', 'Drawing the outlines', 'Framing it on a canvas'],
    default: ['Studying their face and markings', 'Choosing the light', 'Painting the fur', 'Framing it on a canvas'],
  };

  /* ---------- small helpers ---------- */

  const storage = {
    get(store, key) {
      try {
        return JSON.parse(store.getItem(key));
      } catch {
        return null;
      }
    },
    set(store, key, value) {
      try {
        store.setItem(key, JSON.stringify(value));
      } catch {
        /* private mode or full: previews still work for this visit */
      }
    },
  };

  function uuid() {
    if (crypto.randomUUID) return crypto.randomUUID();
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 0x0f) | 0x40;
    b[8] = (b[8] & 0x3f) | 0x80;
    const h = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  }

  function deviceId() {
    let id = storage.get(localStorage, 'sk:device');
    if (!id) {
      id = uuid();
      storage.set(localStorage, 'sk:device', id);
    }
    return id;
  }

  function cookie(name) {
    const m = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return m ? decodeURIComponent(m[1]) : undefined;
  }

  /** Publishes a custom event for the Meta custom pixel. Returns the event id. */
  function track(name, data = {}) {
    const eventId = data.event_id || uuid();
    try {
      window.Shopify?.analytics?.publish?.(name, { ...data, event_id: eventId, page_url: location.href });
    } catch {
      /* analytics must never break the preview */
    }
    return eventId;
  }

  const previews = {
    all() {
      return storage.get(localStorage, STORE_KEY) || {};
    },
    get(id) {
      return this.all()[id] || null;
    },
    save(p) {
      const all = this.all();
      all[p.preview_id] = { ...p, saved_at: Date.now() };
      const ids = Object.keys(all).sort((a, b) => all[b].saved_at - all[a].saved_at);
      ids.slice(10).forEach((id) => delete all[id]);
      storage.set(localStorage, STORE_KEY, all);
    },
  };

  class ApiError extends Error {
    constructor(status, code, message, extra = {}) {
      super(message);
      this.status = status;
      this.code = code;
      Object.assign(this, extra);
    }
  }

  async function apiFetch(path, init = {}, timeoutMs = 60000) {
    if (!API) throw new ApiError(503, 'not_configured', 'Free previews are taking a short break. Please try again soon.');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    let res;
    try {
      res = await fetch(`${API}${path}`, {
        ...init,
        signal: controller.signal,
        headers: { 'x-device-id': deviceId(), ...(init.headers || {}) },
      });
    } catch (err) {
      throw new ApiError(0, 'network', "Your connection dropped. Let's try that again.");
    } finally {
      clearTimeout(timer);
    }
    const body = await res.json().catch(() => ({}));
    if (res.status === 202) return { pending: true };
    if (!res.ok) {
      const e = body.error || {};
      throw new ApiError(res.status, e.code || 'server_error', e.message || 'Something went wrong. Please try again.', e);
    }
    return body;
  }

  /** Decodes, auto-rotates and shrinks the photo in the browser before upload. */
  async function shrinkPhoto(file) {
    let bitmap;
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      // Some browsers can't decode HEIC; an <img> sometimes can.
      bitmap = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new ApiError(415, 'unsupported_type', 'Please choose a JPG or PNG photo.'));
        img.src = URL.createObjectURL(file);
      });
    }
    const w = bitmap.width;
    const h = bitmap.height;
    const scale = Math.min(1, MAX_SIDE / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(w * scale);
    canvas.height = Math.round(h * scale);
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (bitmap.close) bitmap.close();
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) throw new ApiError(415, 'unsupported_type', 'Please choose a JPG or PNG photo.');
    return { blob, url: URL.createObjectURL(blob), width: w, height: h };
  }

  /* ---------- placeholder mode (no API configured) ---------- */

  const fake = {
    async upload() {
      await new Promise((r) => setTimeout(r, 700));
      return { upload_id: `up_placeholder${Date.now()}`, photo_url: null, check: { face_clear: true, tip: '' } };
    },
    async preview(photoUrl, style, options) {
      await new Promise((r) => setTimeout(r, 6000));
      const img = await new Promise((resolve) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.src = photoUrl;
      });
      const portrait = drawPlaceholder(img, 900, 1200, false);
      const mockup = drawPlaceholder(img, 900, 1200, true);
      return {
        preview_id: `pv_placeholder${Date.now()}`,
        style,
        style_label: styleById(style)?.label || style,
        options,
        portrait_url: portrait,
        mockup_url: mockup,
        photo_url: photoUrl,
        previews_left_today: 2,
        placeholder: true,
      };
    },
  };

  function drawPlaceholder(img, w, h, onWall) {
    const c = document.createElement('canvas');
    c.width = w;
    c.height = h;
    const g = c.getContext('2d');
    const box = onWall ? { x: 195, y: 190, w: 510, h: 680 } : { x: 0, y: 0, w, h };
    if (onWall) {
      g.fillStyle = '#ece4d7';
      g.fillRect(0, 0, w, h);
      g.fillStyle = '#8f5f31';
      g.fillRect(100, 985, 720, 215);
      g.shadowColor = 'rgba(60,44,30,.35)';
      g.shadowBlur = 30;
      g.shadowOffsetX = 12;
      g.shadowOffsetY = 20;
    }
    const s = Math.max(box.w / img.width, box.h / img.height);
    const sw = box.w / s;
    const sh = box.h / s;
    g.filter = 'sepia(0.6) saturate(1.3) contrast(1.05)';
    g.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, box.x, box.y, box.w, box.h);
    g.filter = 'none';
    g.shadowColor = 'transparent';
    g.font = '600 22px "Plus Jakarta Sans", sans-serif';
    g.textAlign = 'right';
    g.fillStyle = 'rgba(255,255,255,.85)';
    g.shadowColor = 'rgba(0,0,0,.5)';
    g.shadowBlur = 4;
    g.fillText('Sunnykeep · preview (placeholder)', w - 30, h - 30);
    return c.toDataURL('image/jpeg', 0.85);
  }

  /** Where "message us" goes: Facebook Messenger, else the contact email. */
  // Borderline photos (API check.warning). Clearly blurry ones are refused by the API instead.
  const WARNINGS = {
    blurry: {
      title: 'This photo is a little blurry',
      text: 'The portrait may come out soft. A sharper photo gives the best likeness, but you can still use this one.',
    },
    face_unclear: {
      title: "We can't see their face clearly",
      text: 'The portrait may not look quite like them. A photo with their face toward the camera works best, but you can still use this one.',
    },
  };

  function messageUsUrl() {
    if (CONFIG.messengerUrl) return CONFIG.messengerUrl;
    if (CONFIG.contactEmail) return `mailto:${CONFIG.contactEmail}?subject=${encodeURIComponent('Two-pet preview')}`;
    return '';
  }

  function styleById(id) {
    return (CONFIG.styles || []).find((s) => s.id === id);
  }

  /* ---------- Turnstile (bot check), loaded only when the app first opens ---------- */

  const turnstile = {
    widgetId: null,
    waiting: [],
    token: null,
    load(container) {
      if (PLACEHOLDER || !CONFIG.turnstileSiteKey || this.loading) return;
      this.loading = true;
      window.skTurnstileReady = () => {
        this.widgetId = window.turnstile.render(container, {
          sitekey: CONFIG.turnstileSiteKey,
          appearance: 'interaction-only',
          callback: (token) => {
            this.token = token;
            this.waiting.splice(0).forEach((fn) => fn(token));
          },
          'error-callback': () => this.waiting.splice(0).forEach((fn) => fn(null)),
          // Tokens last 5 minutes; Turnstile fetches a new one itself, so drop the old one.
          'expired-callback': () => {
            this.token = null;
          },
        });
      };
      const s = document.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit&onload=skTurnstileReady';
      s.async = true;
      document.head.appendChild(s);
    },
    /** Resolves with a fresh single-use token (or null after 20 s). */
    get() {
      if (PLACEHOLDER || !CONFIG.turnstileSiteKey) return Promise.resolve(null);
      if (this.token) {
        const t = this.token;
        this.token = null;
        return Promise.resolve(t);
      }
      return new Promise((resolve) => {
        const timer = setTimeout(() => resolve(null), 20000);
        this.waiting.push((t) => {
          clearTimeout(timer);
          this.token = null;
          resolve(t);
        });
      });
    },
    reset() {
      if (this.widgetId !== null && window.turnstile) window.turnstile.reset(this.widgetId);
    },
  };

  /* ---------- the app ---------- */

  class PreviewApp extends HTMLElement {
    connectedCallback() {
      this.input = document.getElementById('SkPhotoInput');
      this.$ = (sel) => this.querySelector(sel);
      this.state = {
        style: 'royal',
        cloak: 'crimson',
        upload: null, // {upload_id, photo_url}
        uploadPromise: null,
        localPhotoUrl: null,
        preview: null,
        view: CONFIG.revealDefault === 'closeup' ? 'closeup' : 'wall',
        email: storage.get(localStorage, EMAIL_KEY),
        pendingEmail: null,
        busy: false,
      };
      this.renderStyleChips();
      this.bind();
    }

    bind() {
      document.addEventListener('click', (e) => {
        const opener = e.target.closest('[data-sk-open]');
        if (!opener) return;
        e.preventDefault();
        if (opener.dataset.skStyle) this.setStyle(opener.dataset.skStyle);
        this.opener = opener;
        this.pickPhoto();
      });
      this.input.addEventListener('change', () => {
        const file = this.input.files && this.input.files[0];
        this.input.value = '';
        if (file) this.startWithFile(file);
      });
      this.$('[data-skp-back]').addEventListener('click', () => this.back());
      this.$('[data-skp-change]').addEventListener('click', () => this.pickPhoto());
      this.querySelectorAll('[data-skp-repick]').forEach((b) => b.addEventListener('click', () => this.pickPhoto()));
      this.$('[data-skp-use-anyway]').addEventListener('click', () => {
        this.state.warningAccepted = true;
        this.$('[data-skp-warning]').hidden = true;
        this.setPhotoStatus('Using this photo', '');
        this.updateCreateButton();
      });
      this.$('[data-skp-create]').addEventListener('click', () => this.create());
      this.$('[data-skp-retry]').addEventListener('click', () => this.create());
      this.$('[data-skp-another]').addEventListener('click', () => this.another());
      this.$('[data-skp-share]').addEventListener('click', () => this.share());
      this.$('[data-skp-early-email]').addEventListener('submit', (e) => this.earlyEmail(e));
      this.$('[data-skp-gate]').addEventListener('submit', (e) => this.submitGate(e));
      this.$('[data-skp-gate-cancel]').addEventListener('click', () => this.closeGate());
      this.querySelectorAll('[data-cloak]').forEach((b) =>
        b.addEventListener('click', () => {
          this.state.cloak = b.dataset.cloak;
          this.querySelectorAll('[data-cloak]').forEach((x) => x.setAttribute('aria-checked', String(x === b)));
        }),
      );
      this.querySelectorAll('.skp__seg-btn').forEach((b) => b.addEventListener('click', () => this.setView(b.dataset.view)));
      this.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') this.back();
      });
      window.addEventListener('popstate', () => {
        if (!this.hidden) this.close(true);
      });
    }

    renderStyleChips() {
      const wrap = this.$('[data-skp-styles]');
      wrap.innerHTML = '';
      (CONFIG.styles || []).forEach((s) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'sk-chip';
        b.setAttribute('role', 'radio');
        b.dataset.style = s.id;
        b.textContent = s.label;
        b.addEventListener('click', () => this.setStyle(s.id));
        wrap.appendChild(b);
      });
      this.setStyle(this.state.style);
    }

    setStyle(id) {
      if (!styleById(id)) return;
      this.state.style = id;
      this.querySelectorAll('[data-skp-styles] .sk-chip').forEach((c) =>
        c.setAttribute('aria-checked', String(c.dataset.style === id)),
      );
      this.$('[data-skp-cloaks]').hidden = id !== 'royal';
      this.$('[data-skp-dates-field]').hidden = id !== 'memorial';
    }

    pickPhoto() {
      this.input.click();
    }

    open() {
      if (!this.hidden) return;
      this.hidden = false;
      document.documentElement.classList.add('sk-noscroll');
      history.pushState({ skPreview: true }, '');
      turnstile.load(this.$('[data-skp-turnstile]'));
    }

    close(fromHistory = false) {
      this.hidden = true;
      document.documentElement.classList.remove('sk-noscroll');
      this.closeGate();
      if (!fromHistory && history.state && history.state.skPreview) history.back();
      if (this.opener) this.opener.focus({ preventScroll: true });
    }

    back() {
      if (!this.$('[data-skp-sheet]').hidden) return this.closeGate();
      if (this.screen === 'reveal' || this.screen === 'limit') return this.show('setup');
      if (this.screen === 'paint' && this.state.busy) return; // stay while it paints
      this.close();
    }

    show(name) {
      this.screen = name;
      this.querySelectorAll('[data-skp-screen]').forEach((s) => (s.hidden = s.dataset.skpScreen !== name));
      const titles = { setup: 'Your free preview', paint: 'Creating your preview', reveal: 'Your free preview', limit: 'Your free preview' };
      this.$('#SkpTitle').textContent = titles[name];
      this.$('[data-skp-step]').textContent = name === 'setup' ? '1 of 2' : '';
      this.$('.skp').scrollTop = 0;
    }

    /* ----- step 1: photo ----- */

    async startWithFile(file) {
      this.open();
      this.show('setup');
      this.state.upload = null;
      this.updateCreateButton();
      const err = this.$('[data-skp-photo-error]');
      err.hidden = true;
      this.$('[data-skp-message-us]').hidden = true;
      this.$('[data-skp-blurry]').hidden = true;
      this.$('[data-skp-warning]').hidden = true;
      this.state.warningAccepted = false;
      this.setPhotoStatus('Checking your photo…', '');
      if (file.size > 20 * 1024 * 1024) return this.photoFailed('That photo is over 20 MB. Please choose a smaller one.');

      let shrunk;
      try {
        shrunk = await shrinkPhoto(file);
      } catch (e) {
        return this.photoFailed(e.message || 'Please choose a JPG or PNG photo.');
      }
      if (this.state.localPhotoUrl) URL.revokeObjectURL(this.state.localPhotoUrl);
      this.state.localPhotoUrl = shrunk.url;
      this.$('[data-skp-thumb]').src = shrunk.url;

      const eventId = track('sk_preview_started', { style: this.state.style });
      const send = async () => {
        const form = new FormData();
        form.append('photo', shrunk.blob, 'pet.jpg');
        form.append('turnstile_token', (await turnstile.get()) || '');
        form.append('event_id', eventId);
        form.append('page_url', location.href);
        if (cookie('_fbp')) form.append('fbp', cookie('_fbp'));
        if (cookie('_fbc')) form.append('fbc', cookie('_fbc'));
        try {
          return await apiFetch('/v1/uploads', { method: 'POST', body: form }, 60000);
        } finally {
          turnstile.reset();
        }
      };
      const promise = (async () => {
        if (PLACEHOLDER) return fake.upload();
        try {
          return await send();
        } catch (e) {
          // An expired or already-used bot-check token: one quiet retry with a fresh one.
          if (e.code !== 'bot_check_failed') throw e;
          return send();
        }
      })();
      this.state.uploadPromise = promise;
      try {
        const up = await promise;
        if (this.state.uploadPromise !== promise) return;
        this.state.upload = up;
        const warning = up.check?.warning;
        if (warning && WARNINGS[warning]) {
          // Borderline photo: nothing is painted until the shopper decides.
          this.setPhotoStatus(WARNINGS[warning].title, '');
          this.$('[data-skp-warning-text]').textContent = WARNINGS[warning].text;
          this.$('[data-skp-warning]').hidden = false;
        } else {
          const tip = up.check && !up.check.face_clear && up.check.tip;
          this.setPhotoStatus(tip ? 'Photo works' : 'Photo looks great', tip ? `Tip: ${tip}` : '✓ Face clear and well lit', !tip);
        }
        this.updateCreateButton();
      } catch (e) {
        if (this.state.uploadPromise !== promise) return;
        this.photoFailed(e.message, e.tip, e.code);
      }
    }

    setPhotoStatus(title, note, good = false) {
      this.$('[data-skp-photo-title]').textContent = title;
      const n = this.$('[data-skp-photo-note]');
      n.textContent = note;
      n.classList.toggle('is-good', good);
    }

    photoFailed(message, tip, code) {
      this.setPhotoStatus("Let's try another photo", '');
      const err = this.$('[data-skp-photo-error]');
      // Two pets isn't a dead end: the message says to message us, and the link does it.
      err.textContent = tip && code !== 'too_many_pets' ? `${message} ${tip}` : message;
      err.hidden = false;
      const msg = this.$('[data-skp-message-us]');
      const href = code === 'too_many_pets' ? messageUsUrl() : '';
      msg.hidden = !href;
      if (href) msg.href = href;
      this.$('[data-skp-blurry]').hidden = code !== 'too_blurry';
      this.state.upload = null;
      this.updateCreateButton();
    }

    updateCreateButton() {
      const b = this.$('[data-skp-create]');
      const needsOk = this.state.upload?.check?.warning && !this.state.warningAccepted;
      b.disabled = !this.state.upload || this.state.busy || Boolean(needsOk);
    }

    /* ----- step 2: create ----- */

    options() {
      const o = {};
      if (this.state.style === 'royal') o.cloak = this.state.cloak;
      const name = this.$('[data-skp-name]').value.trim();
      if (name) o.name = name;
      const dates = this.$('[data-skp-dates]').value.trim();
      if (dates && this.state.style === 'memorial') o.dates = dates;
      return o;
    }

    async create() {
      if (!this.state.upload || this.state.busy) return;
      // Decision: the first preview needs no email; ask before the second style. The API
      // enforces the same rule across visits (email_required), which also opens this sheet.
      if (!this.state.email && this.state.preview) return this.openGate('create');

      this.state.busy = true;
      this.updateCreateButton();
      this.show('paint');
      this.startPainting();
      const requestId = uuid();
      const eventId = uuid();
      const style = this.state.style;
      const options = this.options();
      try {
        let result;
        if (PLACEHOLDER) {
          result = await fake.preview(this.state.localPhotoUrl, style, options);
        } else {
          result = await this.requestPreview({ requestId, eventId, style, options });
        }
        this.finishPainting();
        this.state.preview = result;
        if (!result.placeholder) previews.save(result);
        track('sk_preview_generated', { event_id: eventId, style, preview_id: result.preview_id });
        if (this.state.pendingEmail) this.sendLead(this.state.pendingEmail.email, this.state.pendingEmail.consent);
        this.reveal();
      } catch (e) {
        this.stopPainting();
        this.handleCreateError(e);
      } finally {
        this.state.busy = false;
        this.updateCreateButton();
      }
    }

    async requestPreview({ requestId, eventId, style, options }) {
      const body = JSON.stringify({
        upload_id: this.state.upload.upload_id,
        style,
        options,
        request_id: requestId,
        email: this.state.email || undefined,
        event_id: eventId,
        page_url: location.href,
        fbp: cookie('_fbp'),
        fbc: cookie('_fbc'),
      });
      const post = () => apiFetch('/v1/previews', { method: 'POST', headers: { 'content-type': 'application/json' }, body }, 150000);
      let res;
      try {
        res = await post();
      } catch (e) {
        if (e.code !== 'network') throw e;
        res = { pending: true };
      }
      // Dropped connection or still rendering: ask for the same request until it's done.
      const deadline = Date.now() + 150000;
      while (res.pending && Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 4000));
        try {
          res = await apiFetch(`/v1/previews/jobs/${requestId}`, {}, 15000);
        } catch (e) {
          if (e.code === 'network') continue;
          if (e.status === 404) res = await post();
          else throw e;
        }
        if (res.status === 'failed') res = await post();
      }
      if (res.pending || !res.preview_id) throw new ApiError(0, 'network', "Your connection dropped. Let's try that again.");
      return res;
    }

    handleCreateError(e) {
      if (e.code === 'email_required') {
        this.show('setup');
        return this.openGate('create');
      }
      if (e.code === 'daily_limit' || e.code === 'busy_today') return this.showLimit(e.message);
      if (e.code === 'upload_expired' || e.code === 'not_your_upload' || e.code === 'model_refused') {
        this.show('setup');
        return this.photoFailed(e.message);
      }
      const err = this.$('[data-skp-paint-error]');
      err.textContent = e.message || 'Something went wrong. Please try again.';
      err.hidden = false;
      this.$('[data-skp-retry]').hidden = false;
    }

    startPainting() {
      const steps = STEPS[this.state.style] || STEPS.default;
      const list = this.$('[data-skp-paint-steps]');
      list.innerHTML = '';
      steps.forEach((s) => {
        const li = document.createElement('li');
        li.textContent = s;
        list.appendChild(li);
      });
      this.$('[data-skp-paint-img]').src = this.state.localPhotoUrl;
      this.$('[data-skp-paint-error]').hidden = true;
      this.$('[data-skp-retry]').hidden = true;
      this.$('[data-skp-early-email]').hidden = Boolean(this.state.email);
      const started = Date.now();
      const tick = () => {
        const t = (Date.now() - started) / 1000;
        const pct = Math.min(95, Math.round(100 * (1 - Math.exp(-t / (EXPECTED_SECONDS / 2.2)))));
        this.setProgress(pct);
        const left = Math.round(EXPECTED_SECONDS - t);
        this.$('[data-skp-eta]').textContent = left > 3 ? `About ${left} seconds left` : 'Almost there…';
        const idx = Math.min(steps.length - 1, Math.floor((pct / 100) * steps.length));
        [...list.children].forEach((li, i) => {
          li.className = i < idx ? 'is-done' : i === idx ? 'is-now' : '';
        });
        this.$('[data-skp-paint-title]').textContent = `${steps[idx]}…`;
      };
      tick();
      this.paintTimer = setInterval(tick, 500);
    }

    setProgress(pct) {
      this.$('[data-skp-progress-fill]').style.width = `${pct}%`;
      this.$('[data-skp-progress]').setAttribute('aria-valuenow', String(pct));
    }

    stopPainting() {
      clearInterval(this.paintTimer);
    }

    finishPainting() {
      this.stopPainting();
      this.setProgress(100);
    }

    /* ----- step 3: reveal ----- */

    reveal() {
      const p = this.state.preview;
      const style = styleById(p.style);
      this.show('reveal');
      const name = p.options && p.options.name;
      const title = this.$('[data-skp-reveal-title]');
      title.textContent = name ? `Meet ${name}` : `Your ${style ? style.label : ''} portrait`;
      this.setView(this.state.view);
      const art = this.$('[data-skp-reveal-art]');
      art.classList.remove('is-new');
      void art.offsetWidth; // restart the one-time reveal for each new preview
      art.classList.add('is-new');
      const order = this.$('[data-skp-order]');
      order.textContent = style && style.priceFrom ? `Order this portrait · from ${style.priceFrom}` : 'Order this portrait';
      order.href = orderUrl(p);
      order.onclick = () => track('sk_order_clicked', { preview_id: p.preview_id, style: p.style });
      const left = p.previews_left_today;
      this.$('[data-skp-left]').textContent =
        typeof left === 'number' ? (left > 0 ? `${left} more free ${left === 1 ? 'preview' : 'previews'} today` : 'No more free previews today') : '';
      this.$('[data-skp-another]').disabled = left === 0;
      this.$('[data-skp-announce]').textContent = 'Your preview is ready.';
      title.focus({ preventScroll: true });
    }

    setView(view) {
      const p = this.state.preview;
      if (!p) return;
      this.state.view = view;
      const img = this.$('[data-skp-reveal-img]');
      const style = styleById(p.style);
      const srcs = { wall: p.mockup_url, closeup: p.portrait_url, photo: this.state.localPhotoUrl || p.photo_url };
      img.src = srcs[view];
      // The wall is a room scene we made: label it (the shopper's own preview carries the corner mark).
      this.$('[data-skp-example]').hidden = view !== 'wall';
      img.alt =
        view === 'photo'
          ? 'Your original photo'
          : `Instant preview: your pet as a ${style ? style.label : ''} portrait${view === 'wall' ? ' on a canvas' : ''}`;
      this.querySelectorAll('.skp__seg-btn').forEach((b) => b.setAttribute('aria-selected', String(b.dataset.view === view)));
    }

    another() {
      if (!this.state.email) return this.openGate('another');
      this.show('setup');
    }

    async share() {
      const p = this.state.preview;
      if (!p) return;
      const text = 'Look what Sunnykeep made from my pet photo';
      try {
        if (navigator.canShare && !p.placeholder) {
          const blob = await (await fetch(p.mockup_url)).blob();
          const file = new File([blob], 'sunnykeep-preview.jpg', { type: 'image/jpeg' });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], text, url: location.origin + location.pathname });
            return track('sk_preview_shared', { preview_id: p.preview_id });
          }
        }
        if (navigator.share) {
          await navigator.share({ text, url: location.origin + location.pathname });
          return track('sk_preview_shared', { preview_id: p.preview_id });
        }
        await navigator.clipboard.writeText(location.origin + location.pathname);
        this.$('[data-skp-left]').textContent = 'Link copied.';
      } catch {
        /* share sheet closed */
      }
    }

    showLimit(message) {
      this.show('limit');
      this.$('[data-skp-limit-msg]').textContent = message;
      const style = styleById(this.state.style);
      this.$('[data-skp-limit-order]').href = (style && style.productUrl) || '/collections/all';
    }

    /* ----- email ----- */

    openGate(next) {
      this.gateNext = next;
      const sheet = this.$('[data-skp-sheet]');
      sheet.hidden = false;
      this.$('[data-skp-gate-error]').hidden = true;
      this.$('[data-skp-gate-email]').value = this.state.email || '';
      this.$('#SkpGateTitle').focus();
    }

    closeGate() {
      this.$('[data-skp-sheet]').hidden = true;
    }

    async submitGate(e) {
      e.preventDefault();
      const input = this.$('[data-skp-gate-email]');
      const email = input.value.trim();
      const err = this.$('[data-skp-gate-error]');
      if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(email)) {
        err.textContent = 'Please check your email address.';
        err.hidden = false;
        input.focus();
        return;
      }
      const ok = await this.sendLead(email, this.$('[data-skp-gate-consent]').checked);
      if (!ok) {
        err.textContent = "We couldn't save that just now. Please try again.";
        err.hidden = false;
        return;
      }
      this.closeGate();
      if (this.gateNext === 'create') this.create();
      else this.show('setup');
    }

    async earlyEmail(e) {
      e.preventDefault();
      const email = this.$('[data-skp-email-input]').value.trim();
      const msg = this.$('[data-skp-email-msg]');
      if (!/^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(email)) {
        msg.textContent = 'Please check your email address.';
        return;
      }
      this.state.email = email;
      storage.set(localStorage, EMAIL_KEY, email);
      this.state.pendingEmail = { email, consent: this.$('[data-skp-consent]').checked };
      msg.textContent = "Thanks. We'll send it as soon as it's ready.";
    }

    async sendLead(email, consent) {
      this.state.email = email;
      storage.set(localStorage, EMAIL_KEY, email);
      this.state.pendingEmail = null;
      const p = this.state.preview;
      const eventId = track('sk_email_given', { preview_id: p && p.preview_id });
      if (PLACEHOLDER) return true;
      try {
        await apiFetch(
          '/v1/leads',
          {
            method: 'POST',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({
              email,
              consent: Boolean(consent),
              preview_id: p && !p.placeholder ? p.preview_id : undefined,
              order_url: p ? new URL(orderUrl(p, true), location.origin).href : undefined,
              event_id: eventId,
              page_url: location.href,
              fbp: cookie('_fbp'),
              fbc: cookie('_fbc'),
            }),
          },
          20000,
        );
        return true;
      } catch {
        return false;
      }
    }
  }

  /** Product link for a preview. `portable` adds the image URLs so the link works on another device (emails). */
  function orderUrl(p, portable = false) {
    const style = styleById(p.style);
    const base = (style && style.productUrl) || '/collections/all';
    const u = new URL(base, location.origin);
    u.searchParams.set('sk_preview', p.preview_id);
    if (portable) {
      u.searchParams.set('sk_style', p.style);
      if (p.options && p.options.cloak) u.searchParams.set('sk_cloak', p.options.cloak);
      if (p.options && p.options.name) u.searchParams.set('sk_name', p.options.name);
      u.searchParams.set('sk_m', p.mockup_url);
      u.searchParams.set('sk_po', p.portrait_url);
      u.searchParams.set('sk_ph', p.photo_url);
    }
    return u.pathname + u.search;
  }

  if (!customElements.get('sk-preview-app')) customElements.define('sk-preview-app', PreviewApp);

  // ?style=pop-art (e.g. from the home page's style list) preselects that style on every
  // upload button on the page.
  const urlStyle = new URLSearchParams(location.search).get('style');
  if (urlStyle && styleById(urlStyle)) {
    document.querySelectorAll('[data-sk-open]').forEach((el) => {
      el.dataset.skStyle = urlStyle;
    });
  }

  // Express checkout buttons in the cart drawer are injected by Shopify on page load. When the
  // drawer's HTML is replaced after an Ajax cart change, ask Shopify to render them again.
  function initExpressButtons() {
    try {
      window.Shopify?.StorefrontExpressButtons?.initialize?.();
      window.Shopify?.PaymentButton?.init?.();
    } catch {
      /* the buttons are optional; Checkout always works */
    }
  }
  const drawerRoot = document.getElementById('CartDrawer');
  if (drawerRoot && 'MutationObserver' in window) {
    let pending;
    new MutationObserver(() => {
      clearTimeout(pending);
      pending = setTimeout(() => {
        if (drawerRoot.querySelector('.additional-checkout-buttons')) initExpressButtons();
      }, 50);
    }).observe(drawerRoot, { childList: true, subtree: false });
  }

  // Cart drawer upsell (snippets/sk-cart-extras.liquid): add the ornament with the same art.
  document.addEventListener('click', async (e) => {
    const button = e.target.closest('[data-sk-add-ornament]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    button.setAttribute('aria-disabled', 'true');
    const drawer = document.querySelector('cart-drawer');
    try {
      const body = {
        items: [{ id: Number(button.dataset.skAddOrnament), quantity: 1, properties: JSON.parse(button.dataset.properties || '{}') }],
      };
      if (drawer) body.sections = drawer.getSectionsToRender().map((s) => s.id).join(',');
      const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || 'add failed');
      track('sk_upsell_added', { where: 'cart_drawer' });
      if (drawer && data.sections) drawer.renderContents(data);
      else location.reload();
    } catch {
      button.removeAttribute('aria-disabled');
    }
  });

  // Shared with the product page and cart (assets/sk-product.js).
  window.Sunnykeep = Object.assign(window.Sunnykeep || {}, {
    config: CONFIG,
    previews,
    track,
    uuid,
    deviceId,
    apiFetch,
    shrinkPhoto,
    turnstile,
    cookie,
    isPlaceholder: PLACEHOLDER,
    apiUrl: API,
  });
  document.dispatchEvent(new CustomEvent('sunnykeep:ready'));

  // Landing pages count as ViewContent too (spec); product pages get it from Shopify's
  // product_viewed via the Meta app, so only the landing layout publishes this.
  if (document.body.classList.contains('sk-landing')) {
    track('sk_landing_viewed', { page: location.pathname });
  }
})();
